import { Box } from "@mui/material";
import { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Circle, Polygon, Polyline, Tooltip, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { useTranslate } from "../../Translate/TranslateContext";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import RectangleGardenLayer from "./RectangleGardenLayer";
import UndoIcon from "@mui/icons-material/Undo";
import { IconButton } from "@mui/material";
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const gardenIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const wsIconOrange = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const wsIconRed = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

function isValidCoord(value) {
  return value !== null && value !== undefined && Number.isFinite(Number(value));
}

function roundCoord(value) {
  return Number(Number(value).toFixed(6));
}

function hectaresToRadiusMeters(hectares) {
  if (!hectares) return 0;
  return Math.sqrt((Number(hectares) * 10000) / Math.PI);
}

function getPointCenter(points) {
  if (!points?.length) return null;

  const lat = points.reduce((sum, p) => sum + Number(p[0]), 0) / points.length;
  const lon = points.reduce((sum, p) => sum + Number(p[1]), 0) / points.length;

  return [roundCoord(lat), roundCoord(lon)];
}

function haversineMeters(lat1, lon1, lat2, lon2) {
  if (!isValidCoord(lat1) || !isValidCoord(lon1) || !isValidCoord(lat2) || !isValidCoord(lon2)) return null;

  const toRad = (v) => Number(v) * Math.PI / 180;
  const R = 6371000;
  const dLat = toRad(Number(lat2) - Number(lat1));
  const dLon = toRad(Number(lon2) - Number(lon1));

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(m) {
  if (!Number.isFinite(Number(m))) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function getRectCenter(bounds) {
  const [nw, se] = bounds;
  return [roundCoord((Number(nw[0]) + Number(se[0])) / 2), roundCoord((Number(nw[1]) + Number(se[1])) / 2)];
}

function GeocoderControl({ translate }) {
  const map = useMap();

  useEffect(() => {
    if (!L.Control.geocoder) return;

    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: true,
      placeholder: translate("search_location"),
      position: "topleft",
    });

    geocoder.on("markgeocode", (e) => {
      const center = e.geocode.center;
      map.flyTo([center.lat, center.lng], 14, { animate: true, duration: 1.5 });
    });

    geocoder.addTo(map);

    return () => {
      geocoder.remove();
    };
  }, [map, translate]);

  return null;
}

function MapClickHandler({ enabled, onClick }) {
  useMapEvents({
    click(e) {
      if (!enabled || !e?.latlng) return;
      onClick(e.latlng);
    },
  });

  return null;
}

function RectMoveHandler({ rectDragRef, pendingRectRef, setRectBounds, commitRectBounds }) {
  const map = useMapEvents({
    mousemove(e) {
      if (!rectDragRef.current.active || !rectDragRef.current.last || !e?.latlng) return;

      const last = rectDragRef.current.last;
      const dLat = e.latlng.lat - last.lat;
      const dLng = e.latlng.lng - last.lng;

      if (Math.abs(dLat) < 0.000001 && Math.abs(dLng) < 0.000001) return;

      setRectBounds((prev) => {
        const next = [
          [roundCoord(prev[0][0] + dLat), roundCoord(prev[0][1] + dLng)],
          [roundCoord(prev[1][0] + dLat), roundCoord(prev[1][1] + dLng)],
        ];

        pendingRectRef.current = next;
        return next;
      });

      rectDragRef.current.last = e.latlng;
    },

    mouseup() {
      rectDragRef.current.active = false;
      rectDragRef.current.last = null;
      map.dragging.enable();

      if (pendingRectRef.current) {
        commitRectBounds(pendingRectRef.current);
        pendingRectRef.current = null;
      }
    },

    mouseleave() {
      rectDragRef.current.active = false;
      rectDragRef.current.last = null;
      map.dragging.enable();

      if (pendingRectRef.current) {
        commitRectBounds(pendingRectRef.current);
        pendingRectRef.current = null;
      }
    },
  });

  return null;
}

export default function GardenMap({ garden, setGarden, weatherStations = [] }) {
  const { translate } = useTranslate();

  const isRect = garden.geometry_type === "rectangle" || garden.garden_shape === 1;
  const isPolygon = garden.geometry_type === "polygon" || garden.garden_shape === 2;
  const isCircle = !isRect && !isPolygon;

  const initialLat = isValidCoord(garden.latitude_center) ? Number(garden.latitude_center) : 49.0;
  const initialLon = isValidCoord(garden.longitude_center) ? Number(garden.longitude_center) : 14.0;
  const gardenName = garden.garden_name || garden.name || translate("garden_center");

  const [center, setCenter] = useState([roundCoord(initialLat), roundCoord(initialLon)]);

  const [polygonPoints, setPolygonPoints] = useState(() => {
    if (garden.geometry_type === "polygon" && Array.isArray(garden.geometry?.coordinates)) {
      return garden.geometry.coordinates.map((p) => [roundCoord(p[0]), roundCoord(p[1])]);
    }

    return [];
  });

  const [rectBounds, setRectBounds] = useState(() => {
    if (garden.geometry_type === "rectangle" && Array.isArray(garden.geometry?.coordinates) && garden.geometry.coordinates.length >= 4) {
      const coords = garden.geometry.coordinates;
      return [coords[0], coords[2]];
    }

    return [
      [roundCoord(initialLat + 0.002), roundCoord(initialLon - 0.002)],
      [roundCoord(initialLat - 0.002), roundCoord(initialLon + 0.002)],
    ];
  });

  const rectDragRef = useRef({ active: false, last: null });
  const pendingRectRef = useRef(null);

  const commitPolygonPoints = (points) => {
    const nextCenter = getPointCenter(points);

    setGarden({
      ...garden,
      latitude_center: nextCenter?.[0] ?? garden.latitude_center,
      longitude_center: nextCenter?.[1] ?? garden.longitude_center,
      geometry_type: "polygon",
      garden_shape: 2,
      geometry: {
        coordinates: points,
      },
    });
  };

  const commitRectBounds = (bounds) => {
    const nextCenter = getRectCenter(bounds);
    const nw = bounds[0];
    const se = bounds[1];

    setGarden({
      ...garden,
      latitude_center: nextCenter[0],
      longitude_center: nextCenter[1],
      geometry_type: "rectangle",
      garden_shape: 1,
      geometry: {
        coordinates: [
          [nw[0], nw[1]],
          [nw[0], se[1]],
          [se[0], se[1]],
          [se[0], nw[1]],
        ],
      },
    });
  };

  useEffect(() => {
    if (!isCircle) return;

    if (!isValidCoord(garden.latitude_center) || !isValidCoord(garden.longitude_center)) return;

    const nextCenter = [roundCoord(garden.latitude_center), roundCoord(garden.longitude_center)];

    if (center[0] === nextCenter[0] && center[1] === nextCenter[1]) return;

    setCenter(nextCenter);
  }, [garden.latitude_center, garden.longitude_center, isCircle]);

  useEffect(() => {
    if (!isRect) return;

    if (!isValidCoord(garden.latitude_center) || !isValidCoord(garden.longitude_center)) return;

    const currentCenter = getRectCenter(rectBounds);
    const nextLat = roundCoord(garden.latitude_center);
    const nextLon = roundCoord(garden.longitude_center);

    if (currentCenter[0] === nextLat && currentCenter[1] === nextLon) return;

    const latOffset = nextLat - currentCenter[0];
    const lonOffset = nextLon - currentCenter[1];

    setRectBounds([
      [roundCoord(rectBounds[0][0] + latOffset), roundCoord(rectBounds[0][1] + lonOffset)],
      [roundCoord(rectBounds[1][0] + latOffset), roundCoord(rectBounds[1][1] + lonOffset)],
    ]);
  }, [garden.latitude_center, garden.longitude_center, isRect]);

  useEffect(() => {
    if (!isCircle || rectDragRef.current.active) return;

    const nextLat = roundCoord(center[0]);
    const nextLon = roundCoord(center[1]);
    const currentLat = isValidCoord(garden.latitude_center) ? roundCoord(garden.latitude_center) : null;
    const currentLon = isValidCoord(garden.longitude_center) ? roundCoord(garden.longitude_center) : null;

    if (currentLat === nextLat && currentLon === nextLon) return;

    setGarden({
      ...garden,
      latitude_center: nextLat,
      longitude_center: nextLon,
      geometry_type: "circle",
      garden_shape: 0,
      geometry: {
        center: [nextLat, nextLon],
        radius_m: hectaresToRadiusMeters(garden.garden_area_hectares || 1),
      },
    });
  }, [isCircle, center]);

  const effectiveCenter = useMemo(() => {
    if (isRect) return getRectCenter(rectBounds);
    if (isPolygon) return getPointCenter(polygonPoints) || center;
    return center;
  }, [isRect, isPolygon, rectBounds, polygonPoints, center]);

  const validWeatherStations = useMemo(
    () => (weatherStations || []).filter((ws) => ws && isValidCoord(ws.latitude) && isValidCoord(ws.longitude)),
    [weatherStations]
  );

  const radius = hectaresToRadiusMeters(garden.garden_area_hectares || 1);

  return (
    <Box sx={{ flex: 1, minWidth: 320, height: 500, position: "relative" }}>
      
{isPolygon && polygonPoints.length > 0 && (
  <IconButton
    sx={{
      position: "absolute",
      top: 12,
      right: 12,
      zIndex: 1000,
      backgroundColor: "#FFFAF1",
    }}
    onClick={() => {
      const nextPoints =
        polygonPoints.slice(0, -1);

      setPolygonPoints(nextPoints);
      commitPolygonPoints(nextPoints);
    }}
  >
    <UndoIcon />
  </IconButton>
)}
      <MapContainer center={effectiveCenter} zoom={13} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution="&copy; Esri"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        <TileLayer
  attribution="&copy; Esri"
  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
/>
        
        <GeocoderControl translate={translate} />

        <MapClickHandler
          enabled
          onClick={(latlng) => {
            if (isRect) {
              const currentCenter = getRectCenter(rectBounds);
              const latOffset = latlng.lat - currentCenter[0];
              const lonOffset = latlng.lng - currentCenter[1];

              const nextBounds = [
                [roundCoord(rectBounds[0][0] + latOffset), roundCoord(rectBounds[0][1] + lonOffset)],
                [roundCoord(rectBounds[1][0] + latOffset), roundCoord(rectBounds[1][1] + lonOffset)],
              ];

              setRectBounds(nextBounds);
              commitRectBounds(nextBounds);
              return;
            }

            if (isPolygon) {
              const nextPoints = [...polygonPoints, [roundCoord(latlng.lat), roundCoord(latlng.lng)]];
              setPolygonPoints(nextPoints);
              commitPolygonPoints(nextPoints);
              return;
            }

            setCenter([roundCoord(latlng.lat), roundCoord(latlng.lng)]);
          }}
        />

        <RectMoveHandler
          rectDragRef={rectDragRef}
          pendingRectRef={pendingRectRef}
          setRectBounds={setRectBounds}
          commitRectBounds={commitRectBounds}
        />

        {isCircle && (
          <>
            <Marker
              position={center}
              icon={gardenIcon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const pos = e.target.getLatLng();
                  setCenter([roundCoord(pos.lat), roundCoord(pos.lng)]);
                },
              }}
            >
            
            </Marker>

            <Circle center={center} radius={radius} pathOptions={{ color: "#24582A", fillColor: "#24582A", fillOpacity: 0.28 }}>
              
            </Circle>
          </>
        )}

        {isRect && (
          <RectangleGardenLayer
            rectBounds={rectBounds}
            rectDragRef={rectDragRef}
            pendingRectRef={pendingRectRef}
            setRectBounds={setRectBounds}
            commitRectBounds={commitRectBounds}
          />
        )}

        {isPolygon && (
          <>
            {polygonPoints.length >= 3 && (
              <Polygon positions={polygonPoints} pathOptions={{ color: "#24582A", fillColor: "#24582A", fillOpacity: 0.28 }}>
                
              </Polygon>
            )}

            {polygonPoints.length === 2 && (
              <Polyline positions={polygonPoints} pathOptions={{ color: "#24582A" }}>
               
              </Polyline>
            )}

            {polygonPoints.map((point, index) => (
              <Marker
                key={`${point[0]}-${point[1]}-${index}`}
                position={point}
                icon={gardenIcon}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const pos = e.target.getLatLng();
                    const nextPoints = polygonPoints.map((p, i) => (i === index ? [roundCoord(pos.lat), roundCoord(pos.lng)] : p));

                    setPolygonPoints(nextPoints);
                    commitPolygonPoints(nextPoints);
                  },
                }}
              >
                
              </Marker>
            ))}
          </>
        )}

        {validWeatherStations.map((ws) => {
          const lat = Number(ws.latitude);
          const lon = Number(ws.longitude);
          const isSelected = Number(ws.ws_id) === Number(garden.preferred_ws_id);
          const dist = haversineMeters(effectiveCenter[0], effectiveCenter[1], lat, lon);
          const stationName = ws.name || ws.station_name || ws.ws_name || `${translate("weather_station")} ${ws.ws_id}`;

          return (
            <Marker
              key={ws.ws_id ?? `${lat}-${lon}`}
              position={[lat, lon]}
              icon={isSelected ? wsIconRed : wsIconOrange}
              eventHandlers={{
                click: () => setGarden({ ...garden, preferred_ws_id: ws.ws_id }),
              }}
            >
              
            </Marker>
          );
        })}
      </MapContainer>
    </Box>
  );
}